import { Controller } from '@hotwired/stimulus'

// I-DND-01: manual order of a list's rows (<tbody>, rows carry data-reorder-id). Drag the ⋮⋮ handle (native HTML5
// drag & drop) or use the ↑/↓ buttons; every change PATCHes all ids in the new order to the Reorder operation.
// ponytail: no touch dragging (HTML5 DnD has none); ↑/↓ cover touch and keyboard.
export default class ReorderController extends Controller<HTMLElement> {
  static override values = { url: String, failed: String }
  declare readonly urlValue: string
  declare readonly failedValue: string

  private dragged: HTMLElement | null = null
  private before = ''
  private origin: Element | null = null
  private saving: Promise<void> = Promise.resolve()
  private saved: string[] = []

  override connect(): void {
    this.saved = this.ids()
    this.element.addEventListener('dragstart', this.onDragStart)
    this.element.addEventListener('dragover', this.onDragOver)
    this.element.addEventListener('drop', this.onDrop)
    this.element.addEventListener('dragend', this.onDragEnd)
  }

  override disconnect(): void {
    this.element.removeEventListener('dragstart', this.onDragStart)
    this.element.removeEventListener('dragover', this.onDragOver)
    this.element.removeEventListener('drop', this.onDrop)
    this.element.removeEventListener('dragend', this.onDragEnd)
  }

  up(event: Event): void {
    this.move(event, (row) => row.previousElementSibling, (row, other) => other.before(row))
  }

  down(event: Event): void {
    this.move(event, (row) => row.nextElementSibling, (row, other) => other.after(row))
  }

  private move(event: Event, sibling: (row: Element) => Element | null, place: (row: Element, other: Element) => void): void {
    const button = event.currentTarget as HTMLElement
    const row = button.closest('[data-reorder-id]')
    const other = row && sibling(row)
    if (!row || !other) return
    place(row, other)
    button.focus() // moving the node drops focus; the same button keeps it, so ↑↑↑ works
    void this.save()
  }

  private readonly onDragStart = (event: DragEvent): void => {
    const handle = event.target instanceof Element ? event.target.closest('[data-reorder-handle]') : null // text drag: no
    this.dragged = handle?.closest<HTMLElement>('[data-reorder-id]') ?? null
    if (!this.dragged || !event.dataTransfer) return
    this.before = this.ids().join(',')
    this.origin = this.dragged.nextElementSibling
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', this.dragged.dataset['reorderId'] ?? '') // Firefox starts no drag without data
    event.dataTransfer.setDragImage(this.dragged, 0, 0)
    this.dragged.classList.add('opacity-50')
  }

  private readonly onDragOver = (event: DragEvent): void => {
    const target = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-reorder-id]') : null
    if (!this.dragged || !target) return
    event.preventDefault()
    if (target === this.dragged) return
    const box = target.getBoundingClientRect()
    if (event.clientY < box.top + box.height / 2) target.before(this.dragged)
    else target.after(this.dragged)
  }

  private readonly onDrop = (event: DragEvent): void => {
    if (this.dragged) event.preventDefault()
  }

  private readonly onDragEnd = (event: DragEvent): void => {
    if (!this.dragged) return
    this.dragged.classList.remove('opacity-50')
    // Esc or a drop outside the list: the row goes back, nothing is saved.
    if (event.dataTransfer?.dropEffect === 'none') this.element.insertBefore(this.dragged, this.origin)
    this.dragged = null
    if (this.ids().join(',') !== this.before) void this.save()
  }

  private ids(): string[] {
    return Array.from(this.element.querySelectorAll<HTMLElement>('[data-reorder-id]'), (row) => row.dataset['reorderId'] ?? '')
  }

  // Saves run one after another, each with the order at its turn, so a fast ↑↑↑ can't land out of order.
  private save(): Promise<void> {
    this.saving = this.saving.then(() => this.send())
    return this.saving
  }

  private async send(): Promise<void> {
    const ids = this.ids()
    const body = new FormData()
    for (const id of ids) body.append('ids[]', id)
    const token = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
    const response = await fetch(this.urlValue, {
      method: 'PATCH', body, redirect: 'manual', // an expired session redirects to login: that is a failure, not a save
      headers: { 'X-CSRF-Token': token, Accept: 'text/plain' },
    })
      .catch(() => null)
    if (response?.status === 204) {
      this.saved = ids
      return
    }
    window.alert(this.failedValue)
    // I-UXF-orderwork-4: inside Hromadná úprava a reload would drop the unsaved edits — the rows go back to the last
    // saved order instead. ponytail: elsewhere the server's order wins; a reload shows it.
    if (this.element.closest('form')) this.restore()
    else window.location.reload()
  }

  private restore(): void {
    const rows = new Map(Array.from(this.element.querySelectorAll<HTMLElement>('[data-reorder-id]'), (row) => [row.dataset['reorderId'], row]))
    for (const id of this.saved) {
      const row = rows.get(id)
      if (row) this.element.append(row)
    }
  }
}
